import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ApiClient, { HttpResponse } from "../../config/ApiClient";
import { ApiListResponse, Users } from "../../object-actions/types/types";
import { truncateMessage } from "../../utils/notificationUtils";

const Coaches: React.FC = () => {
    const [coaches, setCoaches] = useState<Users[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCoaches = async () => {
            try {
                setLoading(true);
                const response: HttpResponse<ApiListResponse<"Users">> = await ApiClient.get(
                    "/api/users/search-coaches?organization=smsp&detail=1"
                );
                if (response.success && response.data) {
                    setCoaches(response.data.results);
                } else {
                    setError(response.error || "Failed to load coach information");
                }
            } catch (err) {
                console.error("Error fetching coaches:", err);
                setError("Failed to load coach information. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchCoaches();
    }, []);

    return (
        <Box sx={{ mb: 8 }}>
            <Typography
                variant="h3"
                component="h2"
                sx={{
                    textAlign: "center",
                    mb: 6,
                    fontWeight: "bold"
                }}
            >
                Meet Your Coaches
            </Typography>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={60} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && coaches.length === 0 && (
                <Alert severity="info" sx={{ mb: 4 }}>
                    No coaches found. Please check back later.
                </Alert>
            )}

            {!loading && !error && coaches.length > 0 && (
                <Grid container spacing={4}>
                    {coaches.map((coach) => (
                        <Grid item xs={12} sm={6} md={4} key={coach.id}>
                            <Box
                                component={coach.username ? Link : "div"}
                                to={coach.username ? `/coaches/${coach.username}` : undefined}
                                sx={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    height: "100%",
                                    "&:hover": {
                                        textDecoration: "none"
                                    }
                                }}
                            >
                                <Card
                                    elevation={3}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "transform 0.2s ease-in-out",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: 6
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            pt: 3,
                                            pb: 2
                                        }}
                                    >
                                        <Avatar
                                            alt={`${coach.first_name} ${coach.last_name}`}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                fontSize: "2rem",
                                                fontWeight: "bold"
                                            }}
                                            src={
                                                coach.photo ||
                                                [coach.first_name, coach.last_name].join(" ")
                                            }
                                        />
                                    </Box>

                                    <CardContent
                                        sx={{
                                            flexGrow: 1,
                                            display: "flex",
                                            flexDirection: "column"
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                mb: 1,
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                color: "primary.main"
                                            }}
                                        >
                                            {coach.first_name} {coach.last_name}
                                        </Typography>


                                        {coach.summary && (
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.primary",
                                                    textAlign: "center"
                                                }}
                                            >
                                                {coach.summary}
                                            </Typography>
                                        )}

                                        {coach.bio && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "text.secondary"
                                                }}
                                            >
                                                {truncateMessage(coach.bio, 150)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default Coaches;
